/**
 * @file `Pager` — a panel-agnostic numbered pagination control.
 * @module @labcraft/admin/ui/kernel/pager
 *
 * Drop-in for any offset-paged panel: give it the 1-based current page,
 * the total page count, and an `onPage` callback. Renders a summary on the
 * left (record count + `Page P of C`, when `total` is supplied) and Prev /
 * numbered pages (windowed with ellipses for long ranges) / Next on the
 * right, styled by the shared `.pagination` rules in `panels.css`. Renders
 * nothing only when there's a single page *and* no `total` to summarise.
 * Purely presentational — the panel owns page state and data fetching,
 * exactly like {@link LearnersFilters} owns none of its own.
 */

import type { JSX } from 'react'

/**
 * Props for {@link Pager}.
 *
 * @property page - 1-based current page.
 * @property pageCount - Total number of pages (≥ 1).
 * @property onPage - Called with the target page when a control is clicked.
 * @property total - Total record count across all pages. When supplied, a
 *   summary (`N records · Page P of C`) renders on the left of the bar; the
 *   `Page P of C` half is dropped when there's only one page.
 */
export type PagerProps = {
  page: number
  pageCount: number
  onPage: (page: number) => void
  total?: number
}

/**
 * Render the pager bar: a record/page summary on the left and the numbered
 * nav on the right. Returns nothing only when there's a single page and no
 * `total` to show (nothing to render).
 */
export function Pager({
  page,
  pageCount,
  onPage,
  total,
}: PagerProps): JSX.Element | null {
  const showNav = pageCount > 1
  if (!showNav && total === undefined) return null
  const items = pagerWindow(page, pageCount)
  return (
    <nav className="pagination" aria-label="Pagination">
      {total !== undefined && (
        <span className="pagination__summary">
          {total.toLocaleString()} {total === 1 ? 'record' : 'records'}
          {showNav &&
            ` · Page ${page.toLocaleString()} of ${pageCount.toLocaleString()}`}
        </span>
      )}
      {showNav && (
        <span className="pagination__nav-group">
          <button
            type="button"
            className="pagination__nav"
            disabled={page <= 1}
            aria-label="Previous page"
            onClick={() => {
              onPage(page - 1)
            }}>
            ‹
          </button>
          {items.map((it, i) =>
            it === 'ellipsis' ? (
              <span
                key={`gap-${i}`}
                className="pagination__ellipsis"
                aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={it}
                type="button"
                className="pagination__page"
                aria-label={`Page ${it}`}
                aria-current={it === page ? 'page' : undefined}
                onClick={() => {
                  onPage(it)
                }}>
                {it}
              </button>
            ),
          )}
          <button
            type="button"
            className="pagination__nav"
            disabled={page >= pageCount}
            aria-label="Next page"
            onClick={() => {
              onPage(page + 1)
            }}>
            ›
          </button>
        </span>
      )}
    </nav>
  )
}

/**
 * The windowed page list: always first, last, current, and current ±1,
 * with an `'ellipsis'` marker standing in for each collapsed gap. Small
 * ranges render every page (no gaps to collapse).
 *
 * @param page - 1-based current page.
 * @param pageCount - Total pages (≥ 1).
 * @returns Page numbers interleaved with `'ellipsis'` gap markers.
 */
function pagerWindow(
  page: number,
  pageCount: number,
): Array<number | 'ellipsis'> {
  const shown = [...new Set([1, page - 1, page, page + 1, pageCount])]
    .filter(n => n >= 1 && n <= pageCount)
    .sort((a, b) => a - b)

  const out: Array<number | 'ellipsis'> = []
  let prev = 0
  for (const n of shown) {
    if (n - prev > 1) out.push('ellipsis')
    out.push(n)
    prev = n
  }
  return out
}
